import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";

function normalizeNumberInput(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

function normalizeRowForForm(row) {
  return {
    name: row.name || "",
    category: row.category || "",
    category_adjusted: row.category_adjusted || "",
    unit: row.unit || "",
    calculation_type: row.calculation_type || "",
    coverage_value: normalizeNumberInput(row.coverage_value),
    coverage_unit: row.coverage_unit || "",
    quantity: normalizeNumberInput(row.quantity),
    price_dkk_ex_vat: normalizeNumberInput(row.price_dkk_ex_vat),
    price_dkk_inc_vat: normalizeNumberInput(row.price_dkk_inc_vat),
    price_sek_ex_vat: normalizeNumberInput(row.price_sek_ex_vat),
    price_sek_inc_vat: normalizeNumberInput(row.price_sek_inc_vat),
    ean: row.ean || "",
    sku: row.sku || "",
    suggested_labour_activity: row.suggested_labour_activity || "",
    is_active: !!row.is_active,
  };
}

function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

export default function MaterialLibraryPage() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingRows, setSavingRows] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");

  const [newRow, setNewRow] = useState({
    name: "",
    category: "",
    category_adjusted: "",
    unit: "m2",
    calculation_type: "area",
    coverage_value: "",
    coverage_unit: "",
    quantity: "",
    price_dkk_ex_vat: "",
    price_dkk_inc_vat: "",
    price_sek_ex_vat: "",
    price_sek_inc_vat: "",
    ean: "",
    sku: "",
    suggested_labour_activity: "",
  });

  const saveTimersRef = useRef({});

  async function fetchData() {
    setLoading(true);

    const { data, error } = await supabase
      .from("material_library")
      .select("*")
      .order("category", { ascending: true })
      .order("name", { ascending: true });

    if (error) {
      console.error(error);
    } else {
      const nextRows = data || [];
      setRows(nextRows);

      const nextDrafts = {};
      nextRows.forEach((row) => {
        nextDrafts[row.id] = normalizeRowForForm(row);
      });
      setDrafts(nextDrafts);
    }

    setLoading(false);
  }

  useEffect(() => {
    fetchData();

    return () => {
      Object.values(saveTimersRef.current).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  async function saveRow(id) {
    const draft = drafts[id];
    if (!draft) return;

    setSavingRows((prev) => ({ ...prev, [id]: true }));
    setSaveStatus((prev) => ({ ...prev, [id]: "saving" }));

    const payload = {
      name: draft.name.trim(),
      category: draft.category.trim() || null,
      category_adjusted: draft.category_adjusted.trim() || null,
      unit: draft.unit.trim() || null,
      calculation_type: draft.calculation_type.trim() || null,
      coverage_value: toNumberOrNull(draft.coverage_value),
      coverage_unit: draft.coverage_unit.trim() || null,
      quantity: toNumberOrNull(draft.quantity),
      price_dkk_ex_vat: toNumberOrNull(draft.price_dkk_ex_vat),
      price_dkk_inc_vat: toNumberOrNull(draft.price_dkk_inc_vat),
      price_sek_ex_vat: toNumberOrNull(draft.price_sek_ex_vat),
      price_sek_inc_vat: toNumberOrNull(draft.price_sek_inc_vat),
      ean: draft.ean.trim() || null,
      sku: draft.sku.trim() || null,
      suggested_labour_activity: draft.suggested_labour_activity.trim() || null,
      is_active: !!draft.is_active,
    };

    const { error } = await supabase
      .from("material_library")
      .update(payload)
      .eq("id", id);

    if (error) {
      console.error(error);
      setSaveStatus((prev) => ({ ...prev, [id]: "error" }));
    } else {
      setRows((prev) =>
        prev.map((row) =>
          row.id === id
            ? {
                ...row,
                ...payload,
              }
            : row
        )
      );

      setSaveStatus((prev) => ({ ...prev, [id]: "saved" }));

      window.clearTimeout(saveTimersRef.current[id]);
      saveTimersRef.current[id] = window.setTimeout(() => {
        setSaveStatus((prev) => ({ ...prev, [id]: "" }));
      }, 1200);
    }

    setSavingRows((prev) => ({ ...prev, [id]: false }));
  }

  function scheduleAutosave(id) {
    if (saveTimersRef.current[id]) {
      clearTimeout(saveTimersRef.current[id]);
    }

    setSaveStatus((prev) => ({ ...prev, [id]: "pending" }));

    saveTimersRef.current[id] = setTimeout(() => {
      saveRow(id);
    }, 700);
  }

  function updateDraft(id, field, value) {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));

    scheduleAutosave(id);
  }

  async function createRow() {
    if (!newRow.name.trim()) {
      alert("Name is required");
      return;
    }

    const payload = {
      name: newRow.name.trim(),
      category: newRow.category.trim() || null,
      category_adjusted: newRow.category_adjusted.trim() || null,
      unit: newRow.unit.trim() || null,
      calculation_type: newRow.calculation_type.trim() || null,
      coverage_value: toNumberOrNull(newRow.coverage_value),
      coverage_unit: newRow.coverage_unit.trim() || null,
      quantity: toNumberOrNull(newRow.quantity),
      price_dkk_ex_vat: toNumberOrNull(newRow.price_dkk_ex_vat),
      price_dkk_inc_vat: toNumberOrNull(newRow.price_dkk_inc_vat),
      price_sek_ex_vat: toNumberOrNull(newRow.price_sek_ex_vat),
      price_sek_inc_vat: toNumberOrNull(newRow.price_sek_inc_vat),
      ean: newRow.ean.trim() || null,
      sku: newRow.sku.trim() || null,
      suggested_labour_activity:
        newRow.suggested_labour_activity.trim() || null,
      is_active: true,
    };

    const { error } = await supabase.from("material_library").insert([payload]);

    if (error) {
      console.error(error);
      return;
    }

    setNewRow({
      name: "",
      category: "",
      category_adjusted: "",
      unit: "m2",
      calculation_type: "area",
      coverage_value: "",
      coverage_unit: "",
      quantity: "",
      price_dkk_ex_vat: "",
      price_dkk_inc_vat: "",
      price_sek_ex_vat: "",
      price_sek_inc_vat: "",
      ean: "",
      sku: "",
      suggested_labour_activity: "",
    });

    fetchData();
  }

  const categories = useMemo(() => {
    const values = [...new Set(rows.map((row) => row.category).filter(Boolean))];
    return values.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();

    return rows.filter((row) => {
      const draft = drafts[row.id] || normalizeRowForForm(row);

      const matchesSearch =
        !query ||
        [
          draft.name,
          draft.category,
          draft.category_adjusted,
          draft.unit,
          draft.calculation_type,
          draft.coverage_unit,
          draft.ean,
          draft.sku,
          draft.suggested_labour_activity,
          draft.price_dkk_ex_vat,
          draft.price_dkk_inc_vat,
          draft.price_sek_ex_vat,
          draft.price_sek_inc_vat,
        ]
          .join(" ")
          .toLowerCase()
          .includes(query);

      const matchesCategory =
        categoryFilter === "all" || draft.category === categoryFilter;

      const matchesActive =
        activeFilter === "all" ||
        (activeFilter === "active" && draft.is_active) ||
        (activeFilter === "inactive" && !draft.is_active);

      return matchesSearch && matchesCategory && matchesActive;
    });
  }, [rows, drafts, search, categoryFilter, activeFilter]);

  function renderStatus(id) {
    const status = saveStatus[id];
    if (status === "saving") return "Saving...";
    if (status === "pending") return "Pending";
    if (status === "saved") return "Saved";
    if (status === "error") return "Error";
    return "";
  }

  return (
    <div style={{ padding: 20 }}>
      <h1>Material Library</h1>

      <div
        style={{
          marginBottom: 20,
          padding: 12,
          border: "1px solid #ccc",
          borderRadius: 8,
        }}
      >
        <h3>Add new material</h3>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <input
            type="text"
            placeholder="Name"
            value={newRow.name}
            onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
          />
          <input
            type="text"
            placeholder="Category"
            value={newRow.category}
            onChange={(e) => setNewRow({ ...newRow, category: e.target.value })}
          />
          <input
            type="text"
            placeholder="Adjusted category"
            value={newRow.category_adjusted}
            onChange={(e) =>
              setNewRow({ ...newRow, category_adjusted: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Unit"
            value={newRow.unit}
            onChange={(e) => setNewRow({ ...newRow, unit: e.target.value })}
          />
          <input
            type="text"
            placeholder="Calculation type"
            value={newRow.calculation_type}
            onChange={(e) =>
              setNewRow({ ...newRow, calculation_type: e.target.value })
            }
          />
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <input
            type="number"
            step="0.01"
            placeholder="Coverage value"
            value={newRow.coverage_value}
            onChange={(e) =>
              setNewRow({ ...newRow, coverage_value: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Coverage unit"
            value={newRow.coverage_unit}
            onChange={(e) =>
              setNewRow({ ...newRow, coverage_unit: e.target.value })
            }
          />
          <input
            type="number"
            step="0.01"
            placeholder="Quantity"
            value={newRow.quantity}
            onChange={(e) =>
              setNewRow({ ...newRow, quantity: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="EAN"
            value={newRow.ean}
            onChange={(e) => setNewRow({ ...newRow, ean: e.target.value })}
          />
          <input
            type="text"
            placeholder="SKU"
            value={newRow.sku}
            onChange={(e) => setNewRow({ ...newRow, sku: e.target.value })}
          />
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <input
            type="number"
            step="0.01"
            placeholder="DKK ex VAT"
            value={newRow.price_dkk_ex_vat}
            onChange={(e) =>
              setNewRow({ ...newRow, price_dkk_ex_vat: e.target.value })
            }
          />
          <input
            type="number"
            step="0.01"
            placeholder="DKK inc VAT"
            value={newRow.price_dkk_inc_vat}
            onChange={(e) =>
              setNewRow({ ...newRow, price_dkk_inc_vat: e.target.value })
            }
          />
          <input
            type="number"
            step="0.01"
            placeholder="SEK ex VAT"
            value={newRow.price_sek_ex_vat}
            onChange={(e) =>
              setNewRow({ ...newRow, price_sek_ex_vat: e.target.value })
            }
          />
          <input
            type="number"
            step="0.01"
            placeholder="SEK inc VAT"
            value={newRow.price_sek_inc_vat}
            onChange={(e) =>
              setNewRow({ ...newRow, price_sek_inc_vat: e.target.value })
            }
          />
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Suggested labour activity"
            value={newRow.suggested_labour_activity}
            onChange={(e) =>
              setNewRow({
                ...newRow,
                suggested_labour_activity: e.target.value,
              })
            }
            style={{ minWidth: 320 }}
          />
          <button onClick={createRow}>Add</button>
        </div>
      </div>

      <div
        style={{
          marginBottom: 16,
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          placeholder="Search materials..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 320 }}
        />

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="all">All categories</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>

        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>

        <button
          onClick={() => {
            setSearch("");
            setCategoryFilter("all");
            setActiveFilter("all");
          }}
        >
          Clear filters
        </button>

        <button onClick={fetchData}>Refresh</button>

        <div style={{ marginLeft: "auto", fontWeight: 600 }}>
          Showing {filteredRows.length} / {rows.length}
        </div>
      </div>

      {loading ? (
        <div>Loading material library...</div>
      ) : (
        <table border="1" cellPadding="6" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Adj. Category</th>
              <th>Unit</th>
              <th>Calc Type</th>
              <th>Coverage</th>
              <th>DKK ex</th>
              <th>DKK inc</th>
              <th>SEK ex</th>
              <th>SEK inc</th>
              <th>Labour Hint</th>
              <th>Active</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {filteredRows.map((row) => {
              const draft = drafts[row.id] || normalizeRowForForm(row);

              return (
                <tr key={row.id}>
                  <td>
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(e) => updateDraft(row.id, "name", e.target.value)}
                      style={{ minWidth: 220 }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={draft.category}
                      onChange={(e) =>
                        updateDraft(row.id, "category", e.target.value)
                      }
                      style={{ minWidth: 140 }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={draft.category_adjusted}
                      onChange={(e) =>
                        updateDraft(row.id, "category_adjusted", e.target.value)
                      }
                      style={{ minWidth: 140 }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={draft.unit}
                      onChange={(e) => updateDraft(row.id, "unit", e.target.value)}
                      style={{ width: 70 }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={draft.calculation_type}
                      onChange={(e) =>
                        updateDraft(row.id, "calculation_type", e.target.value)
                      }
                      style={{ width: 90 }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={draft.coverage_value}
                      onChange={(e) =>
                        updateDraft(row.id, "coverage_value", e.target.value)
                      }
                      style={{ width: 80 }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={draft.price_dkk_ex_vat}
                      onChange={(e) =>
                        updateDraft(row.id, "price_dkk_ex_vat", e.target.value)
                      }
                      style={{ width: 90 }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={draft.price_dkk_inc_vat}
                      onChange={(e) =>
                        updateDraft(row.id, "price_dkk_inc_vat", e.target.value)
                      }
                      style={{ width: 90 }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={draft.price_sek_ex_vat}
                      onChange={(e) =>
                        updateDraft(row.id, "price_sek_ex_vat", e.target.value)
                      }
                      style={{ width: 90 }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={draft.price_sek_inc_vat}
                      onChange={(e) =>
                        updateDraft(row.id, "price_sek_inc_vat", e.target.value)
                      }
                      style={{ width: 90 }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={draft.suggested_labour_activity}
                      onChange={(e) =>
                        updateDraft(
                          row.id,
                          "suggested_labour_activity",
                          e.target.value
                        )
                      }
                      style={{ minWidth: 180 }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!draft.is_active}
                      onChange={(e) =>
                        updateDraft(row.id, "is_active", e.target.checked)
                      }
                    />
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {savingRows[row.id] ? "Saving..." : renderStatus(row.id)}
                  </td>
                </tr>
              );
            })}

            {!filteredRows.length && (
              <tr>
                <td colSpan="13" style={{ textAlign: "center", padding: 20 }}>
                  No rows found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}